import { useState } from 'react'
import { Badge, Button, Modal, Nav, Spinner, Table } from 'react-bootstrap'
import { useQuery } from '@apollo/client'
import { GET_AUTOMATION_LOGS, GET_AUTOMATION_SCHEDULED_TODAY, PREVIEW_AUTOMATION_CONTACTS } from '../../../gql/queries/campaigns'
import { Automation, IAutomationLog, AutomationLogStatus } from '../../../types/campaigns'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'

interface ScheduledContact {
    contactId: string
    firstName: string
    lastName: string
    email: string
    phone: string
    dateBirth: string
    alreadySent: boolean
}

interface Props {
    show: boolean
    onHide: () => void
    automation: Automation | null
}

const statusVariant: Record<AutomationLogStatus, string> = {
    SENT: 'success',
    FAILED: 'danger',
    PENDING: 'warning',
    SKIPPED: 'secondary',
}

export const AutomationLogModal = ({ show, onHide, automation }: Props) => {
    const [activeTab, setActiveTab] = useState<'logs' | 'today'>('logs')

    const { data, loading } = useQuery<{ automationLogs: IAutomationLog[] }>(GET_AUTOMATION_LOGS, {
        skip: !show || !automation,
        variables: { automationId: automation?.id },
        fetchPolicy: 'network-only',
    })

    const { data: previewData, loading: previewLoading } = useQuery(PREVIEW_AUTOMATION_CONTACTS, {
        skip: !show || !automation,
        variables: {
            triggerType: automation?.triggerType,
            triggerConfig: automation?.triggerConfig,
            scope: automation?.scope ?? 'PERSONAL',
        },
        fetchPolicy: 'network-only',
    })

    const { data: scheduledData, loading: scheduledLoading } = useQuery<{ automationScheduledToday: ScheduledContact[] }>(
        GET_AUTOMATION_SCHEDULED_TODAY,
        {
            skip: !show || !automation || activeTab !== 'today',
            variables: { automationId: automation?.id },
            fetchPolicy: 'network-only',
        }
    )

    const logs = data?.automationLogs ?? []
    const eligibleCount = previewData?.previewAutomationContacts?.contactCount
    const scheduled = scheduledData?.automationScheduledToday ?? []

    return (
        <Modal show={show} onHide={onHide} size='lg' centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className='bi bi-clock-history me-2'></i>
                    {automation?.name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className='p-0'>
                {/* Stats bar */}
                <div className='d-flex gap-0 border-bottom'>
                    <div className='flex-fill text-center py-3 border-end'>
                        <div className='fs-4 fw-bold text-primary'>{automation?.sentCount ?? 0}</div>
                        <div className='text-muted small'>Sent</div>
                    </div>
                    <div className='flex-fill text-center py-3 border-end'>
                        <div className='fs-4 fw-bold text-secondary'>{automation?.logCount ?? 0}</div>
                        <div className='text-muted small'>Total runs</div>
                    </div>
                    <div className='flex-fill text-center py-3'>
                        {previewLoading ? (
                            <Spinner animation='border' size='sm' className='mt-1' />
                        ) : (
                            <div className={`fs-4 fw-bold ${eligibleCount === 0 ? 'text-warning' : 'text-success'}`}>
                                {eligibleCount ?? '—'}
                            </div>
                        )}
                        <div className='text-muted small'>
                            {automation?.triggerType === 'HAPPY_BIRTHDAY' ? 'With birthday' : 'Eligible today'}
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <Nav variant='tabs' className='px-3 pt-2' activeKey={activeTab} onSelect={(k) => setActiveTab(k as 'logs' | 'today')}>
                    <Nav.Item>
                        <Nav.Link eventKey='logs'>
                            <i className='bi bi-list-ul me-1'></i>Sending history
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey='today'>
                            <i className='bi bi-calendar-day me-1'></i>Scheduled today
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                {/* Log table */}
                {activeTab === 'logs' && (
                    <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                        {loading && (
                            <div className='text-center py-4'>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Loading...
                            </div>
                        )}
                        {!loading && logs.length === 0 && (
                            <div className='text-center text-muted py-5'>
                                <i className='bi bi-inbox' style={{ fontSize: 48 }}></i>
                                <p className='mt-2'>No sending records yet</p>
                            </div>
                        )}
                        {!loading && logs.length > 0 && (
                            <Table hover responsive size='sm' className='mb-0 align-middle'>
                                <thead className='table-light'>
                                    <tr>
                                        <th className='ps-4'>Contact</th>
                                        <th>Channel</th>
                                        <th>Status</th>
                                        <th>Date</th>
                                        <th>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => (
                                        <tr key={log.id}>
                                            <td className='ps-4'>
                                                {log.contact ? (
                                                    <div className='d-flex flex-column gap-1'>
                                                        <span>
                                                            {`${log.contact.firstName} ${log.contact.lastName}`.trim() || '—'}
                                                        </span>
                                                        {log.channel === 'EMAIL' && log.contact.email && (
                                                            <span className='small text-muted'>
                                                                <i className='bi bi-envelope me-1'></i>{log.contact.email}
                                                            </span>
                                                        )}
                                                        {(log.channel === 'SMS' || log.channel === 'WHATSAPP' || log.channel === 'TELEGRAM') && log.contact.phone && (
                                                            <PhoneProviderActions
                                                                phone={log.contact.phone}
                                                                className='d-inline-flex flex-column align-items-start'
                                                                textClassName='small text-muted'
                                                            />
                                                        )}
                                                    </div>
                                                ) : '—'}
                                            </td>
                                            <td>
                                                <Badge bg={log.channel === 'EMAIL' ? 'primary' : 'info'} className='text-white'>
                                                    {log.channel}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge bg={statusVariant[log.status] ?? 'secondary'}>{log.status}</Badge>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {new Date(log.sentAt).toLocaleString()}
                                            </td>
                                            <td>
                                                {log.errorMessage
                                                    ? <span className='text-danger small'>{log.errorMessage}</span>
                                                    : <span className='text-muted'>—</span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </div>
                )}

                {/* Scheduled today tab */}
                {activeTab === 'today' && (
                    <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                        {scheduledLoading && (
                            <div className='text-center py-4'>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Loading...
                            </div>
                        )}
                        {!scheduledLoading && scheduled.length === 0 && (
                            <div className='text-center text-muted py-5'>
                                <i className='bi bi-calendar-x' style={{ fontSize: 48 }}></i>
                                <p className='mt-2'>No contacts scheduled for today</p>
                            </div>
                        )}
                        {!scheduledLoading && scheduled.length > 0 && (
                            <Table hover responsive size='sm' className='mb-0 align-middle'>
                                <thead className='table-light'>
                                    <tr>
                                        <th className='ps-4'>Contact</th>
                                        {automation?.triggerType === 'HAPPY_BIRTHDAY' && <th>Birthday</th>}
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scheduled.map((c) => (
                                        <tr key={c.contactId}>
                                            <td className='ps-4'>
                                                <div className='d-flex flex-column gap-1'>
                                                    <span>{`${c.firstName} ${c.lastName}`.trim() || '—'}</span>
                                                    {automation?.channel === 'EMAIL' && c.email && (
                                                        <span className='small text-muted'>
                                                            <i className='bi bi-envelope me-1'></i>{c.email}
                                                        </span>
                                                    )}
                                                    {(automation?.channel === 'SMS' || automation?.channel === 'WHATSAPP' || automation?.channel === 'TELEGRAM') && c.phone && (
                                                        <PhoneProviderActions
                                                            phone={c.phone}
                                                            className='d-inline-flex flex-column align-items-start'
                                                            textClassName='small text-muted'
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            {automation?.triggerType === 'HAPPY_BIRTHDAY' && (
                                                <td className='text-muted small'>
                                                    {c.dateBirth ? new Date(c.dateBirth + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
                                                </td>
                                            )}
                                            <td>
                                                {c.alreadySent
                                                    ? <Badge bg='success'><i className='bi bi-check me-1'></i>Sent today</Badge>
                                                    : <Badge bg='warning' text='dark'><i className='bi bi-clock me-1'></i>Pending</Badge>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className='justify-content-end'>
                <Button variant='light' onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
}
